/**
 * Router and the session state machine.
 *
 * Routes are real paths so the PWA restores where the learner left off; the
 * server sends the shell for anything that is not /api or an asset.
 */

import "./styles/app.css";
import { api, ApiError } from "./api.ts";
import type { DeferredAnswer } from "./blocks/types.ts";
import { renderComplete } from "./screens/complete.ts";
import { renderLogin } from "./screens/login.ts";
import { renderProgress } from "./screens/progress.ts";
import { renderSettings } from "./screens/settings.ts";
import { runSession } from "./screens/session.ts";
import { renderToday } from "./screens/today.ts";
import { startKeyboardInset } from "./keyboard.ts";
import { cacheState, cachedState, onOutboxChange, startOutboxLoop } from "./store.ts";
import { initTheme } from "./theme.ts";
import { el, mount, primaryButton, shell } from "./ui.ts";

const pendingDeferred = new Map<number, DeferredAnswer[]>();

function navigate(path: string, replace = false): void {
	if (replace) history.replaceState({}, "", path);
	else history.pushState({}, "", path);
	void route();
}

async function route(): Promise<void> {
	const path = window.location.pathname;
	const online = navigator.onLine;

	const sessionMatch = /^\/session\/(\d+)$/.exec(path);
	if (sessionMatch !== null) {
		const id = Number(sessionMatch[1]);
		await runSession(
			id,
			{
				complete: (sessionId, deferred) => {
					pendingDeferred.set(sessionId, deferred);
					navigate(`/complete/${sessionId}`, true);
				},
				exit: () => navigate("/"),
			},
			online,
		);
		return;
	}

	const completeMatch = /^\/complete\/(\d+)$/.exec(path);
	if (completeMatch !== null) {
		const id = Number(completeMatch[1]);
		const deferred = pendingDeferred.get(id) ?? [];
		pendingDeferred.delete(id);
		await renderComplete(id, deferred, {
			openSession: (nextId) => navigate(`/session/${nextId}`),
			backToToday: () => navigate("/"),
		});
		return;
	}

	if (path === "/progress") {
		await renderProgress(() => navigate("/"));
		return;
	}

	if (path === "/settings") {
		await renderSettings(
			() => navigate("/"),
			() => navigate("/", true),
		);
		return;
	}

	await renderTodayScreen(online);
}

async function renderTodayScreen(online: boolean): Promise<void> {
	const handlers = {
		openSession: (id: number): void => navigate(`/session/${id}`),
		openProgress: (): void => navigate("/progress"),
		openSettings: (): void => navigate("/settings"),
		refresh: (): void => {
			void route();
		},
	};

	try {
		const state = await api.state();
		await cacheState(state);
		renderToday(state, handlers, !online);
	} catch (error) {
		if (error instanceof ApiError && error.status === 401) {
			renderLogin(() => navigate("/", true));
			return;
		}
		const cached = await cachedState();
		if (cached !== undefined) {
			renderToday(cached, handlers, true);
			return;
		}
		mount(
			shell({
				eyebrowLeft: "Fala",
				body: [
					el("div", {
						class: "notice",
						text: error instanceof ApiError ? error.message : "No connection, and nothing cached yet.",
					}),
				],
				footer: primaryButton("Try again", handlers.refresh, { variant: "ghost" }),
			}),
		);
	}
}

async function boot(): Promise<void> {
	initTheme();
	startKeyboardInset();
	startOutboxLoop();

	// One persistent banner rather than a toast per failure.
	const banner = el("div", { class: "offline-banner", hidden: true });
	document.body.prepend(banner);
	const updateBanner = (pending: number, stuck: boolean): void => {
		if (!navigator.onLine) {
			banner.hidden = false;
			banner.textContent = pending > 0 ? `Offline · ${pending} answers waiting` : "Offline";
		} else if (stuck) {
			banner.hidden = false;
			banner.textContent = `${pending} answers could not be sent`;
		} else {
			banner.hidden = true;
		}
	};
	onOutboxChange(updateBanner);
	window.addEventListener("online", () => void route());
	window.addEventListener("offline", () => updateBanner(0, false));

	window.addEventListener("popstate", () => void route());

	const { authenticated } = await api.checkAuth().catch(() => ({ authenticated: false }));
	if (!authenticated) {
		renderLogin(() => navigate("/", true));
		return;
	}

	await route();
}

if ("serviceWorker" in navigator && import.meta.env.PROD) {
	window.addEventListener("load", () => {
		void navigator.serviceWorker.register("/sw.js", { scope: "/" });
	});
}

void boot();
